import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileDownload,
  faChevronDown,
  faAward,
} from "@fortawesome/free-solid-svg-icons";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  where,
  getDoc,
  doc,
  getDocs,
  limit,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@mmfestival/shared-components/common/NotificationContext";
import {
  SectionBMMDetail,
  SectionRundown,
} from "@mmfestival/shared-components";
import axios from "axios";
import Iframe from "react-iframe";
// import entire shared data module so we can fall back to either per-category
// mappings (qualificationByCategory / raceInclusionsByCategory) or global defaults
import * as BMMData from "@mmfestival/shared-components/data/BMMDetail";

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const formatDate = (dateString) => {
  if (!dateString) return "Date not specified";
  try {
    let date;
    if (
      typeof dateString === "object" &&
      "toDate" in dateString &&
      typeof dateString.toDate === "function"
    ) {
      date = dateString.toDate();
    } else if (typeof dateString === "object" && "seconds" in dateString) {
      date = new Date(dateString.seconds * 1000);
    } else {
      date = new Date(dateString);
    }
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    return "Date not specified";
  } catch {
    return "Date not specified";
  }
};

// translate status labels (keeps robust checks for both Indonesian and English)
const getStatusKey = (status) => {
  if (!status) return "other";
  const s = String(status).trim().toLowerCase();

  // specific mappings (ID/EN)
  if (
    s === "segera dibuka" ||
    s === "segera" ||
    s === "coming soon" ||
    s === "segera dibuka."
  )
    return "coming_soon";

  if (
    s === "registrasi dibuka" ||
    s === "pendaftaran dibuka" ||
    s === "dibuka" ||
    s === "open" ||
    s === "registration open"
  )
    return "open";

  if (
    s === "registrasi ditutup" ||
    s === "pendaftaran ditutup" ||
    s === "ditutup" ||
    s === "closed" ||
    s === "registration closed"
  )
    return "closed";

  // heuristics
  if (s.includes("segera") || s.includes("coming") || s.includes("soon"))
    return "coming_soon";
  if (s.includes("dibuka") || s.includes("open")) return "open";
  if (s.includes("ditutup") || s.includes("tutup") || s.includes("closed"))
    return "closed";

  return "other";
};

const translateStatus = (status) => {
  const key = getStatusKey(status);
  if (key === "open") return "Registration Open";
  if (key === "closed") return "Registration Closed";
  if (key === "coming_soon") return "Coming Soon";
  return "Registration TBD";
};

// MAPPING EVENT ID KE COLLECTION NAME
const EVENT_COLLECTION_MAP = {
  SMM: "pendaftaranSMM",
  JMM: "pendaftaranJMM",
  BMM: "pendaftaranBMM",
};

// --- HARDCODED PREVIEW MEDIA (HYBRID) ---
// Media berikut selalu dipakai oleh komponen (override nilai di Firestore)
const HARD_PREVIEW = {
  image: "/bg/bali.jpg",
  currentImage: "/bg/bali.jpg",
  logo: "/logo/BMM.png",
  logodetail: "/logo/BMM1.png",
};

export const BMM = React.memo(() => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [viewMode, setViewMode] = useState("map");
  const [collectionName, setCollectionName] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [snapToken, setSnapToken] = useState(null);
  const [isSnapReady, setIsSnapReady] = useState(false);

  // === FUNGSI UNTUK GET COLLECTION NAME DARI MAPPING ===
  const getCollectionName = useCallback((eventId) => {
    if (EVENT_COLLECTION_MAP[eventId]) {
      return EVENT_COLLECTION_MAP[eventId];
    }
    return null;
  }, []);

  /**
   * Archive a registration document instead of deleting it.
   * Writes a document with the same id into archivedRegistrations and removes the original
   * using a batch so the operations happen atomically from the client's POV.
   * On failure it marks the original document for archival retry and does NOT delete it,
   * avoiding irreversible deletion from the client side.
   */
  const archiveRegistration = useCallback(
    async (docRef, docData, originalCollectionName) => {
      if (!docRef) return;
      try {
        const batch = writeBatch(db);
        const archivedRef = doc(db, "archivedRegistrations", docRef.id);

        const payload = {
          ...docData,
          originalCollection: originalCollectionName || null,
          originalDocRefPath: docRef.path,
          archivedAt: serverTimestamp(),
          archivedByUid: user?.uid || null,
        };

        batch.set(archivedRef, payload);
        batch.delete(docRef);

        await batch.commit();
      } catch (e) {
        // Log the failure so developers/ops can see it in console
        console.error("Failed to archive registration (batch):", e);

        // Safer fallback: mark the original doc for archival retry instead of deleting it
        try {
          await updateDoc(docRef, {
            archiveStatus: "failed",
            archiveError: e?.message || String(e),
            archiveRetryRequestedAt: serverTimestamp(),
            // increment count if docData contains previous count
            archiveRetryCount:
              docData && docData.archiveRetryCount
                ? docData.archiveRetryCount + 1
                : 1,
          });
        } catch (markErr) {
          // If marking fails, log it — do NOT delete the original doc
          console.error("Failed to mark doc for archival:", markErr);
        }

        // NOTE: No backend notification here (you indicated there is no such endpoint).
        // If you later add a server endpoint or Cloud Function to process retries,
        // you can call it from here or let the server poll/scan documents with archiveStatus='failed'.
      }
    },
    [user]
  );

  const checkRegistration = useCallback(
    async (collectionName, eventId) => {
      if (!user || !eventId || !collectionName)
        return { isRegistered: false, status: null, token: null };
      try {
        const q = query(
          collection(db, collectionName),
          where("uid", "==", user.uid),
          where("eventId", "==", eventId),
          limit(1)
        );
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const docRef = querySnapshot.docs[0];
          const data = docRef.data();
          const status = data.transaction_status || "unknown";
          const paymentType = data.payment_type || "unknown";
          const harga = data.harga || 0;

          if (
            (status === "settlement" &&
              (paymentType === "free" || harga === 0)) ||
            (status === "pending" && paymentType !== "free") ||
            (status === "settlement" &&
              paymentType !== "free" &&
              data.snapToken)
          ) {
            setRegistrationStatus("registered");
            setTransactionStatus(status);
            setSnapToken(data.snapToken || null);

            if (status === "pending" && data.snapToken && data.orderId) {
              try {
                const response = await axios.get(
                  `${import.meta.env.VITE_API_BASE_URL}/api/payment/status/${
                    data.orderId
                  }`,
                  { params: { eventId } }
                );
                const updatedStatus = response.data.transaction_status;
                if (updatedStatus !== status) {
                  if (
                    updatedStatus === "pending" ||
                    updatedStatus === "settlement"
                  ) {
                    await updateDoc(docRef.ref, {
                      transaction_status: updatedStatus,
                    });
                    setTransactionStatus(updatedStatus);
                    return {
                      isRegistered: true,
                      status: updatedStatus,
                      token: data.snapToken,
                    };
                  } else {
                    // instead of deleting, archive the registration (safer)
                    await archiveRegistration(docRef.ref, data, collectionName);
                    setRegistrationStatus("not_registered");
                    setTransactionStatus(null);
                    setSnapToken(null);
                    return { isRegistered: false, status: null, token: null };
                  }
                }
              } catch (err) {
                console.error("Error checking payment status:", err);
              }
            }
            return { isRegistered: true, status, token: data.snapToken };
          } else {
            // not matching 'registered' conditions -> archive instead of delete (safer)
            await archiveRegistration(docRef.ref, data, collectionName);
            setRegistrationStatus("not_registered");
            setTransactionStatus(null);
            setSnapToken(null);
            return { isRegistered: false, status: null, token: null };
          }
        } else {
          setRegistrationStatus("not_registered");
          return { isRegistered: false, status: null, token: null };
        }
      } catch (err) {
        console.error("Error checking registration:", err);
        return { isRegistered: false, status: null, token: null };
      }
    },
    [user, archiveRegistration]
  );

  const fetchEventFromTrilogy = useCallback(() => {
    const found = TrilogyDetail.find((e) => e.id === "BMM");
    if (!found) return null;

    return {
      id: found.id,
      slug: found.slug,
      title: found.title,
      type: found.type,
      status: translateStatus(found.status ?? ""), // ensure canonical English
      location: found.location,
      // override media with HARD_PREVIEW
      image: HARD_PREVIEW.image,
      currentImage: HARD_PREVIEW.currentImage,
      logo: HARD_PREVIEW.logo || null,
      logodetail: HARD_PREVIEW.logodetail || null,
      aboutEvent: found.aboutEvent,
      websiteUrl: found.websiteUrl,
      eventId: found.eventId,
      price: found.price || 0,

      categories: found.categories.map((c) => ({
        categoryName: c.categoryName,
        cot: c.cot,
        date: c.date,
        description: c.description,
        distance: c.distance,
        elevationGain: c.elevationGain,
        price: c.price,
        foreignerPrice: c.foreignerPrice ?? null,
        time: c.eventDateTimestamp
          ? c.eventDateTimestamp.split(" at ")[1]?.split(" UTC")[0] || ""
          : "",
        gpxFileUrl: c.gpxFileUrl || "",
        mapUrl: c.mapUrl || "",
        // allow Trilogy detail to provide per-category qualification/raceInclusions if available
        qualification: c.qualification || null,
        raceInclusions: c.raceInclusions || null,
      })),
    };
  }, []);

  const fetchEventData = useCallback(async () => {
    try {
      const eventDoc = await getDoc(doc(db, "semuaKegiatan", "BMM"));
      if (eventDoc.exists()) {
        const data = eventDoc.data() || {};
        // build eventData from Firestore but OVERRIDE media with hardcoded preview
        const eventData = {
          id: eventDoc.id,
          slug: data.slug,
          title: data.title,
          type: data.type,
          // Translate status immediately so UI is always in English canonical labels
          status: translateStatus(data.status ?? data.statusText ?? ""),
          location: data.location,
          image: HARD_PREVIEW.image,
          currentImage: HARD_PREVIEW.currentImage,
          logo: HARD_PREVIEW.logo || null,
          logodetail: HARD_PREVIEW.logodetail || null,
          aboutEvent: data.aboutEvent,
          websiteUrl: data.websiteUrl,
          eventId: data.eventId,
          price: data.price || 0,
          categories: Array.isArray(data.categories)
            ? data.categories.map((c) => ({
                categoryName: c.categoryName,
                cot: c.cot,
                date: c.date,
                description: c.description,
                distance: c.distance,
                elevationGain: c.elevationGain,
                price: c.price,
                foreignerPrice: c.foreignerPrice ?? null,
                time: c.eventDateTimestamp
                  ? c.eventDateTimestamp.split(" at ")[1]?.split(" UTC")[0] ||
                    ""
                  : c.time || "",
                gpxFileUrl: c.gpxFileUrl || "",
                mapUrl: c.mapUrl || "",
                // read per-category qualification and inclusions from Firestore if present
                qualification: c.qualification || null,
                raceInclusions: c.raceInclusions || null,
                // support an explicit flag from Firestore to enable/disable entry list per category
                entryListEnabled:
                  typeof c.entryListEnabled !== "undefined"
                    ? c.entryListEnabled
                    : undefined,
              }))
            : [],
          // support an explicit flag from Firestore to enable/disable entry list at event level
          entryListEnabled:
            typeof data.entryListEnabled !== "undefined"
              ? data.entryListEnabled
              : undefined,
        };
        setEvent(eventData);
        setLoading(false);
        return eventData;
      }
    } catch (err) {
      console.info("Firebase did not find BMM. Using TrilogyDetail.", err);
    }

    // fallback to TrilogyDetail (but still override media)
    const fallbackEvent = fetchEventFromTrilogy();
    if (fallbackEvent) {
      setEvent(fallbackEvent);
      setLoading(false);
      return fallbackEvent;
    } else {
      setLoading(false);
      return null;
    }
  }, [fetchEventFromTrilogy]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      const eventData = await fetchEventData();
      if (!eventData || !mounted) return;

      if (eventData.eventId) {
        try {
          const collName = getCollectionName(eventData.eventId);

          if (mounted) {
            setCollectionName(collName);

            if (collName && user) {
              const { isRegistered, status, token } = await checkRegistration(
                collName,
                eventData.eventId
              );
              setRegistrationStatus(
                isRegistered ? "registered" : "not_registered"
              );
              setTransactionStatus(status);
              setSnapToken(token);
            } else if (!collName && user) {
              setRegistrationStatus("not_registered");
            }
          }
        } catch (err) {
          console.error("Error in fetchData:", err);
          if (mounted) setCollectionName(null);
        }
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [fetchEventData, getCollectionName, checkRegistration, user]);

  useEffect(() => {
    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);
    return () => clearInterval(checkSnap);
  }, []);

  const handleBayar = useCallback(async () => {
    if (!isSnapReady || !window.snap) {
      showNotification("error", "Payment gateway not ready.");
      return;
    }
    if (!snapToken) {
      showNotification("error", "Payment token not found.");
      return;
    }
    try {
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          showNotification("success", "Payment successful!");
          const { status } = await checkRegistration(
            collectionName,
            event.eventId
          );
          setTransactionStatus(status);
          navigate("/pesanansaya");
        },
        onPending: async () => {
          showNotification("warning", "Payment is processing.");
          const { status } = await checkRegistration(
            collectionName,
            event.eventId
          );
          setTransactionStatus(status);
          navigate("/pesanansaya");
        },
        onError: () => {
          showNotification("error", "Payment failed.");
        },
        onClose: () => {},
      });
    } catch (err) {
      console.error("Error in payment:", err);
      showNotification("error", "Failed to process payment.");
    }
  }, [
    isSnapReady,
    snapToken,
    collectionName,
    event,
    showNotification,
    navigate,
    checkRegistration,
  ]);

  const handleRegister = useCallback(
    (category) => {
      if (
        !event ||
        !event.title ||
        (category.price == null && event.price == null) ||
        !event.eventId
      ) {
        showNotification("error", "Event data incomplete.");
        return;
      }
      if (!user) {
        showNotification("error", "Please log in first.");
        navigate("/login");
        return;
      }
      if (!collectionName) {
        showNotification("error", "Failed to load event data.");
        console.error("collectionName is null:", { eventId: event.eventId });
        return;
      }
      const title =
        event.type === "seminar"
          ? event.title
          : `${event.title} - ${category.categoryName || ""}`;
      const path =
        event.type === "seminar"
          ? `/formseminar/${event.slug}`
          : `/form/${event.slug}`;
      if (registrationStatus === "registered") {
        if (transactionStatus === "settlement")
          showNotification(
            "info",
            "You are already registered for this event."
          );
        else if (transactionStatus === "pending") handleBayar();
        return;
      }
      const price = category.price ?? event.price ?? 0;
      const foreignerPriceToSend = category.foreignerPrice ?? null;
      navigate(path, {
        state: {
          title,
          price,
          foreignerPrice: foreignerPriceToSend,
          eventId: event.eventId,
        },
      });
    },
    [
      event,
      user,
      collectionName,
      registrationStatus,
      transactionStatus,
      showNotification,
      navigate,
      handleBayar,
    ]
  );

  const handleExportGpx = useCallback(
    debounce(async (gpxFileUrl, selectedCategory, event) => {
      if (!gpxFileUrl) {
        showNotification("error", "GPX file not available.");
        return;
      }
      try {
        const response = await fetch(gpxFileUrl, { mode: "cors" });
        const gpxText = await response.text();
        const blob = new Blob([gpxText], { type: "application/gpx+xml" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const fileName =
          event.type === "seminar"
            ? `${event.title.toLowerCase().replace(/\s+/g, "-")}.gpx`
            : `${event.title.toLowerCase().replace(/\s+/g, "-")}-${
                selectedCategory.distance
              }km.gpx`;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Error exporting GPX:", err);
        showNotification("error", "Failed to download GPX file.");
      }
    }, 500),
    [showNotification]
  );

  const categories = useMemo(() => {
    if (!event?.categories) return [];
    return event.categories;
  }, [event]);

  const selectedCategory = useMemo(
    () => categories[selectedCategoryIndex] || {},
    [categories, selectedCategoryIndex]
  );

  // Helper: normalize many possible formats to an array of strings
  const normalizeToArray = useCallback((raw) => {
    if (!raw && raw !== 0) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
    if (typeof raw === "string") {
      // try parse JSON array string first
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
      } catch (e) {
        // ignore
      }
      // split by newline/semicolon/pipe/comma
      const parts = raw
        .split(/\r?\n|;|\||,/)
        .map((s) => s.trim())
        .filter(Boolean);
      return parts.length ? parts : [raw.trim()];
    }
    if (typeof raw === "object") {
      // object with numeric keys or values
      try {
        const vals = Object.values(raw)
          .map((v) => (v == null ? "" : String(v).trim()))
          .filter(Boolean);
        if (vals.length) return vals;
      } catch (e) {
        // fallback
      }
    }
    return [String(raw)];
  }, []);

  // compute per-category qualification & inclusions, fallback to shared defaults or shared per-category mapping
  const categoryQualification = useMemo(() => {
    // 1) Firestore per-category field
    const perCat = normalizeToArray(selectedCategory?.qualification);
    if (perCat.length) return perCat;

    // 2) shared-data per-category mapping (qualificationByCategory)
    const mapped =
      selectedCategory?.categoryName &&
      BMMData.qualificationByCategory?.[selectedCategory.categoryName];
    const mappedArr = normalizeToArray(mapped);
    if (mappedArr.length) return mappedArr;

    // 3) global fallback (BMMData.qualification)
    return normalizeToArray(BMMData.qualification);
  }, [selectedCategory, normalizeToArray]);

  const categoryInclusions = useMemo(() => {
    // 1) Firestore per-category field
    const perCat = normalizeToArray(selectedCategory?.raceInclusions);
    if (perCat.length) return perCat;

    // 2) shared-data per-category mapping (raceInclusionsByCategory)
    const mapped =
      selectedCategory?.categoryName &&
      BMMData.raceInclusionsByCategory?.[selectedCategory.categoryName];
    const mappedArr = normalizeToArray(mapped);
    if (mappedArr.length) return mappedArr;

    // 3) global fallback (BMMData.raceInclusions)
    return normalizeToArray(BMMData.raceInclusions);
  }, [selectedCategory, normalizeToArray]);

  const isRegistrationOpen = useMemo(
    () => getStatusKey(event?.status) === "open",
    [event]
  );

  const isComingSoon = useMemo(
    () => getStatusKey(event?.status) === "coming_soon" || getStatusKey(event?.status) === "other",
    [event]
  );

  // displayStatus will always use canonical English label
  const displayStatus = useMemo(
    () => translateStatus(event?.status ?? event?.status),
    [event]
  );

  // SEDERHANA: hardcoded toggle for Entry List (sesuai permintaan)
  // Ubah ke `true` jika mau enable, `false` jika mau disable
  const isEntryListEnabled = false;

  const scrollToContent = () => {
    document
      .querySelector(".detail-content")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading)
    return (
      <div className="relative w-full h-screen overflow-hidden bg-gray-200">
        <div className="absolute inset-0 bg-gradient-to-t from-gray-600/100 to-gray-600/20 z-5" />
      </div>
    );

  if (!event)
    return (
      <section className="w-full min-h-screen py-20 text-center text-slate-600 flex-col justify-center relative bg-white">
        <motion.h2
          className="text-xl sm:text-2xl md:text-3xl font-normal uppercase mb-4 text-slate-600"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          EVENT
        </motion.h2>
        <motion.p
          className="text-4xl sm:text-5xl md:text-7xl font-black mx-auto mb-16 text-slate-600 px-4"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          NOT FOUND
        </motion.p>
        <button
          onClick={() => navigate("/")}
          className="text-slate-600 underline"
        >
          Back to Home
        </button>
      </section>
    );

  // local itemVariants to keep animation consistent with SectionBMMDetail
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.08,
        duration: 0.36,
        ease: "easeInOut",
      },
    }),
  };

  return (
    <>
      {/* HERO HEADER */}
      <section
        className="relative w-full min-h-screen text-black text-center flex flex-col justify-center overflow-hidden"
        style={{
          backgroundImage: `url('${HARD_PREVIEW.image}')`,
          backgroundAttachment: "fixed",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* === EFEK ZOOM-IN === */}
        <motion.div
          className="absolute inset-0 z-1 pointer-events-none"
          style={{
            backgroundImage: `url('${HARD_PREVIEW.currentImage}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
          }}
          initial={{ scale: 1 }}
          animate={{ scale: 1.25 }}
          transition={{
            duration: 12,
            ease: "easeOut",
          }}
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-600/100 to-gray-600/20 z-5"></div>

        {/* Logo & Title & Description */}
        <div className="relative text-white text-center z-10 flex flex-col justify-center flex-grow">
          <motion.div
            className="py-2 flex justify-center"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            {HARD_PREVIEW.logodetail && (
              <img
                src={HARD_PREVIEW.logodetail}
                alt={`${event?.title} Logo Detail`}
                className="h-24 md:h-48 w-auto object-contain drop-shadow-2xl"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
          </motion.div>

          <motion.h2
            className="text-sm sm:text-lg md:text-lg font-normal text-white max-w-[800px] mx-auto px-4 leading-relaxed"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            {event?.aboutEvent}
          </motion.h2>
        </div>

        {/* Scroll Down */}
        <div className="absolute bottom-8 left-0 w-full flex justify-center z-20">
          <motion.div
            className="cursor-pointer text-white animate-bounce"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
            onClick={scrollToContent}
          >
            <FontAwesomeIcon icon={faChevronDown} className="text-4xl" />
          </motion.div>
        </div>
      </section>

      {/* DETAIL CONTENT */}
      <section className="detail-content bg-white">
        <div className="w-full">
          {/* KATEGORI TABS */}
          {categories.length > 1 && (
            <div className="md:hidden bg-gray-100 text-base font-normal text-gray-400">
              <div className="flex flex-col divide-y divide-gray-300">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    className={`w-full text-left uppercase px-4 py-4 block transition-colors duration-300 ${
                      selectedCategoryIndex === index
                        ? "bg-cyan-500 font-normal text-white"
                        : "hover:bg-gray-200"
                    }`}
                    onClick={() => setSelectedCategoryIndex(index)}
                  >
                    {category.categoryName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {categories.length > 1 && (
            <div className="hidden md:flex bg-gray-200 text-base font-normal text-gray-400 ">
              <div className="flex w-full">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    className={`flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 ${
                      selectedCategoryIndex === index
                        ? "bg-cyan-500 font-normal text-white"
                        : "hover:bg-gray-200"
                    }`}
                    onClick={() => setSelectedCategoryIndex(index)}
                  >
                    {category.categoryName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* INFO UTAMA - Full-width table with balanced columns */}
          {(event.type === "trail_running" || event.type === "seminar") && (
            <div>
              <div className="hidden md:block">
                <table className="w-full border-collapse table-fixed">
                  <colgroup>
                    <col className="w-[30%]" />
                    <col className="w-[15%]" />
                    <col className="w-[15%]" />
                    <col className="w-[15%]" />
                    <col className="w-[20%]" />
                  </colgroup>
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="py-3 pl-4 lg:pl-[max(1rem,calc((100vw-80rem)/2+1rem))] 2xl:pl-[max(1rem,calc((100vw-96rem)/2+1rem))] text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Start Date
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Flag-Off
                      </th>
                      {event.type === "trail_running" && (
                        <>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Distance
                          </th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Elevation Gain
                          </th>
                          <th className="py-3 pr-4 lg:pr-[max(1rem,calc((100vw-80rem)/2+1rem))] 2xl:pr-[max(1rem,calc((100vw-96rem)/2+1rem))] text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                            COT
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-4 pl-4 lg:pl-[max(1rem,calc((100vw-80rem)/2+1rem))] 2xl:pl-[max(1rem,calc((100vw-96rem)/2+1rem))] text-sm text-gray-700">
                        {formatDate(selectedCategory.date)}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-700">
                        {selectedCategory.time || "-"}
                      </td>
                      {event.type === "trail_running" && (
                        <>
                          <td className="py-4 px-4 text-sm text-gray-700">
                            {selectedCategory.distance != null
                              ? `${selectedCategory.distance} KM`
                              : "-"}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700">
                            {selectedCategory.elevationGain != null
                              ? `${selectedCategory.elevationGain} M`
                              : "-"}
                          </td>
                          <td className="py-4 pr-4 lg:pr-[max(1rem,calc((100vw-80rem)/2+1rem))] 2xl:pr-[max(1rem,calc((100vw-96rem)/2+1rem))] text-right text-sm text-gray-700">
                            {selectedCategory.cot != null
                              ? `${selectedCategory.cot} Hours`
                              : "-"}
                          </td>
                        </>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="md:hidden">
                <div className="space-y-4 py-4 bg-white">
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Start Date
                    </div>
                    <div className="text-sm text-gray-700">
                      {formatDate(selectedCategory.date)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Flag-Off
                    </div>
                    <div className="text-sm text-gray-700">
                      {selectedCategory.time || "-"}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Location
                    </div>
                    <div className="text-sm text-gray-700">
                      {event.location || "-"}
                    </div>
                  </div>
                  {event.type === "trail_running" && (
                    <>
                      <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                        <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                          Distance
                        </div>
                        <div className="text-sm text-gray-700">
                          {selectedCategory.distance != null
                            ? `${selectedCategory.distance} KM`
                            : "-"}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                        <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                          Elevation Gain
                        </div>
                        <div className="text-sm text-gray-700">
                          {selectedCategory.elevationGain != null
                            ? `${selectedCategory.elevationGain} M`
                            : "-"}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pb-3 px-4">
                        <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                          COT
                        </div>
                        <div className="text-sm text-gray-700">
                          {selectedCategory.cot != null
                            ? `${selectedCategory.cot} Hours`
                            : "-"}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* DESKRIPSI */}
          <div className="py-4 mt-8 text-slate-900">
            <div className="mx-auto max-w-7xl 2xl:max-w-[96rem] px-4">




              {/*  <p className="mb-4 text-lg leading-relaxed text-gray-700">
                {event.aboutEvent || "No event description available."}
              </p> */}






              {/* Qualification & Race Inclusions */}
              {event.type === "trail_running" &&
                ((categoryQualification && categoryQualification.length) ||
                  (categoryInclusions && categoryInclusions.length)) && (
                  <motion.div
                    // KEEP outer wrapper white (as original). We'll wrap the internal content (qualification + inclusions) in amber container as requested.
                    className="bg-white rounded-xl border border-slate-200 p-4 mb-4"
                    initial="hidden"
                    whileInView="visible"
                    variants={itemVariants}
                    viewport={{ once: true }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <FontAwesomeIcon
                        icon={faAward}
                        className="text-lg text-cyan-600"
                      />
                      <h2 className="text-xl lg:text-2xl font-semibold text-slate-700">
                        Qualification & Race Inclusions
                      </h2>
                    </div>

                    {/* Intro paragraph under title (from shared data module) */}
                    {BMMData.qualificationIntro && (
                      <p className="text-md text-gray-600 mb-4 whitespace-pre-line">
                        {BMMData.qualificationIntro}
                      </p>
                    )}

                    {/* NEW: Amber container wraps only the content (Qualification + Race Inclusions) */}
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Qualification - bullets like Sacred Features */}
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold text-slate-700">
                            Qualification
                          </h3>
                          {categoryQualification &&
                            categoryQualification.map((q, i) => (
                              <div
                                key={`qual-inline-${i}`}
                                className="flex gap-4"
                              >
                                <div className="flex-shrink-0 mt-2">
                                  <div className="w-2 h-2 rounded-full bg-cyan-600"></div>
                                </div>
                                <div className="flex-1">
                                  <p className="text-slate-700 text-sm leading-snug">
                                    {q}
                                  </p>
                                </div>
                              </div>
                            ))}
                        </div>

                        {/* Race Inclusions - compact pills (horizontal, wrapping) */}
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold text-slate-700">
                            Race Inclusions
                          </h3>
                          <div className="flex flex-wrap gap-3">
                            {categoryInclusions &&
                              categoryInclusions.map((inc, i) => (
                                <div
                                  key={`inc-inline-${i}`}
                                  className="inline-flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-full border border-slate-200"
                                >
                                  <div className="w-2 h-2 rounded-full bg-cyan-600 flex-shrink-0" />
                                  <span className="text-sm font-medium text-slate-700 whitespace-nowrap">
                                    {inc}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

              {event.type === "trail_running" && (
                  <div>
                    {viewMode === "map" &&
                      (selectedCategory.mapUrl ? (
                        <div className="relative w-full h-[600px] rounded-xl overflow-hidden border border-slate-200">
                          <Iframe
                            url={selectedCategory.mapUrl}
                            width="100%"
                            height="600px"
                            className="rounded-xl"
                            display="block"
                            position="relative"
                          />
                        </div>
                      ) : (
                        <div className="relative w-full h-auto rounded-xl overflow-hidden border border-slate-200">
                          <img
                            src={`/map/BMM${selectedCategory.distance}.png`}
                            alt={`Map ${selectedCategory.distance}KM`}
                            className="w-full h-auto object-cover"
                            onError={(e) => (e.target.style.display = "none")}
                          />
                        </div>
                      ))}
                  </div>
                )}
            </div>
          </div>

          {/* TABS MAP (MOBILE) */}
          {event.type === "trail_running" && (
            <div className="md:hidden">
              <div className="flex gap-2 px-4">
                <button
                  className={`flex-1 uppercase py-3 text-sm font-medium rounded-lg transition-colors duration-300 ${
                    viewMode === "map"
                      ? "bg-cyan-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  onClick={() =>
                    handleExportGpx(
                      selectedCategory.gpxFileUrl,
                      selectedCategory,
                      event
                    )
                  }
                >
                  DOWNLOAD GPX
                </button>
              </div>
            </div>
          )}

          {/* CONTAINER STATUS HARGA PESERTA NAVIGASI */}
          <div className="pt-4 md:pt-0 pb-4">
            <div className="mx-auto max-w-7xl 2xl:max-w-[96rem] px-4">
              {/* DESKTOP WRAPPER: only md+ and add 4px side spacing */}
              <div className="hidden md:block">
                {/* DOWNLOAD GPX SECTION - DESKTOP */}


                {/* STATUS, HARGA, PESERTA, NAVIGASI CONTAINER - DESKTOP */}
                <div className="border border-gray-300 rounded-2xl p-4">
                  <div className="grid grid-cols-4 gap-4">
                    {/* Status */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                        Status
                      </p>
                      <div className="flex items-center">
                        <div
                          className={`w-2.5 h-2.5 rounded-full mr-2 ${
                            getStatusKey(event.status) === "open"
                              ? "bg-cyan-500"
                              : getStatusKey(event.status) === "closed"
                              ? "bg-red-500"
                              : "bg-yellow-500"
                          }`}
                        ></div>
                        <span className="text-sm text-gray-700">
                          {displayStatus}
                        </span>
                      </div>
                    </div>

                    {/* Harga desktop */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                        Price
                      </p>
                      <div className="text-sm font-semibold text-cyan-600">
                        {selectedCategory.foreignerPrice != null ? (
                          <div>
                            {(() => {
                              const domesticPrice =
                                selectedCategory.price ?? event.price ?? 0;
                              const domText =
                                domesticPrice > 0
                                  ? `Rp ${domesticPrice.toLocaleString(
                                      "id-ID"
                                    )}`
                                  : "Free";
                              const foreText =
                                selectedCategory.foreignerPrice > 0
                                  ? `Rp ${selectedCategory.foreignerPrice.toLocaleString(
                                      "id-ID"
                                    )}`
                                  : "Free";

                              if (
                                domesticPrice === 0 &&
                                selectedCategory.foreignerPrice === 0
                              )
                                return "Free";
                              if (domesticPrice === 0)
                                return `Free / Foreigner ${foreText.replace(
                                  "Rp ",
                                  ""
                                )}`;
                              if (selectedCategory.foreignerPrice === 0)
                                return `${domText} / Foreigner Free`;

                              return `${domText} / Foreigner ${foreText.replace(
                                "Rp ",
                                ""
                              )}`;
                            })()}
                          </div>
                        ) : (
                          <div>
                            {(() => {
                              const price =
                                selectedCategory.price ?? event.price ?? 0;
                              return price > 0
                                ? `Rp ${price.toLocaleString("id-ID")}`
                                : "Free";
                            })()}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Peserta */}
                    {event.eventId && (
                      <div>
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                          Participants
                        </p>

                        {/* SIMPLE: button is always rendered, but disabled and styled when isEntryListEnabled === false */}
                        <button
                          onClick={() => {
                            if (!isEntryListEnabled) return;
                            navigate(
                              `/entrylist/${event.eventId}/${
                                selectedCategory.distance || "all"
                              }`
                            );
                          }}
                          disabled={!isEntryListEnabled}
                          aria-disabled={!isEntryListEnabled}
                          className={`text-sm transition-colors ${
                            isEntryListEnabled
                              ? "text-cyan-600 hover:text-cyan-700"
                              : "text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          Entry List
                        </button>
                      </div>
                    )}

                    {/* GPX FILE */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                        GPX FILE
                      </p>
                      {selectedCategory.gpxFileUrl && event.type === "trail_running" ? (
                         <button
                           className="text-cyan-600 font-medium flex items-center space-x-2 hover:text-cyan-700 transition-colors"
                           onClick={() =>
                             handleExportGpx(
                               selectedCategory.gpxFileUrl,
                               selectedCategory,
                               event
                             )
                           }
                         >
                           <FontAwesomeIcon icon={faFileDownload} />
                           <span>Download</span>
                         </button>
                      ) : (
                        <span className="text-sm text-gray-400">Not available</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* STATUS, HARGA, PESERTA, NAVIGASI CONTAINER - MOBILE (unchanged) */}
              <div className="md:hidden border border-gray-300 rounded-2xl overflow-hidden">
                {/* Status */}
                <div className="px-4 py-3 border-b border-gray-300 bg-gray-50">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                    Status
                  </p>
                  <div className="flex items-center">
                    <div
                      className={`w-2.5 h-2.5 rounded-full mr-2 ${
                        getStatusKey(event.status) === "open"
                          ? "bg-cyan-500"
                          : getStatusKey(event.status) === "closed"
                          ? "bg-red-500"
                          : "bg-yellow-500"
                      }`}
                    ></div>
                    <span className="text-sm text-gray-700">
                      {displayStatus}
                    </span>
                  </div>
                </div>

                {/* Harga - Mobile */}
                <div className="px-4 py-3 border-b border-gray-300 bg-white">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                    Price
                  </p>
                  <div className="text-sm font-semibold text-cyan-600">
                    {selectedCategory.foreignerPrice != null ? (
                      <div>
                        {(() => {
                          const domesticPrice =
                            selectedCategory.price ?? event.price ?? 0;
                          const domText =
                            domesticPrice > 0
                              ? `Rp ${domesticPrice.toLocaleString("id-ID")}`
                              : "Free";

                          const foreText =
                            selectedCategory.foreignerPrice > 0
                              ? `Rp ${selectedCategory.foreignerPrice.toLocaleString(
                                  "id-ID"
                                )}`
                              : "Free";

                          if (
                            domesticPrice === 0 &&
                            selectedCategory.foreignerPrice === 0
                          )
                            return "Free";
                          if (domesticPrice === 0)
                            return `Free / Foreigner ${foreText.replace(
                              "Rp ",
                              ""
                            )}`;
                          if (selectedCategory.foreignerPrice === 0)
                            return `${domText} / Foreigner Free`;

                          return `${domText} / Foreigner ${foreText.replace(
                            "Rp ",
                            ""
                          )}`;
                        })()}
                      </div>
                    ) : (
                      <div>
                        {(() => {
                          const price =
                            selectedCategory.price ?? event.price ?? 0;
                          return price > 0
                            ? `Rp ${price.toLocaleString("id-ID")}`
                            : "Free";
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Peserta */}
                {event.eventId && (
                  <div className="px-4 py-3 border-b border-gray-300 bg-gray-50">
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                      Participants
                    </p>

                    {/* MOBILE: same simple behavior — button disabled when not enabled */}
                    <button
                      onClick={() => {
                        if (!isEntryListEnabled) return;
                        navigate(
                          `/entrylist/${event.eventId}/${
                            selectedCategory.distance || "all"
                          }`
                        );
                      }}
                      disabled={!isEntryListEnabled}
                      aria-disabled={!isEntryListEnabled}
                      className={`text-sm transition-colors ${
                        isEntryListEnabled
                          ? "text-cyan-600 hover:text-cyan-700"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      Entry List
                    </button>
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* DESKRIPSI KATEGORI */}
          <div className="text-slate-900">
            <div className="mx-auto max-w-7xl 2xl:max-w-[96rem] px-4">
            


               <p className="mb-4 text-lg leading-relaxed text-gray-700">
                {selectedCategory.description || "No description available."}
              </p>



            </div>



            
          </div>

          {event.type === "trail_running" && (
            <SectionBMMDetail selectedCategory={selectedCategory} />
          )}

          {/* TOMBOL DAFTAR */}
          <div className="mt-4">
            <div className="hidden md:block">
              <div className="flex items-center justify-end bg-gray-100 text-base font-normal text-gray-400">
                <div className="flex justify-end space-x-0 divide-x divide-gray-300">
                  {registrationStatus === "registered" &&
                  transactionStatus === "settlement" ? (
                    <span className="flex-1 text-center uppercase px-20 py-4 bg-cyan-600 text-white whitespace-nowrap">
                      YOU ARE REGISTERED
                    </span>
                  ) : registrationStatus === "registered" &&
                    transactionStatus === "pending" ? (
                    <button
                      onClick={handleBayar}
                      className="flex-1 text-center uppercase px-20 py-4 transition-colors duration-300 bg-cyan-600 font-normal text-white whitespace-nowrap hover:bg-cyan-800"
                      disabled={!isSnapReady || !snapToken}
                    >
                      COMPLETE PAYMENT
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRegister(selectedCategory)}
                      className={`flex-1 text-center uppercase px-20 py-4 transition-colors duration-300 whitespace-nowrap ${
                        isRegistrationOpen
                          ? "bg-cyan-600 font-normal text-white hover:bg-cyan-800"
                          : "text-white bg-slate-400 opacity-50 cursor-not-allowed"
                      }`}
                      disabled={!isRegistrationOpen}
                    >
                      {isComingSoon ? "COMING SOON" : isRegistrationOpen ? "REGISTER THIS EVENT" : "REGISTRATION CLOSED"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="md:hidden">
              <div className="bg-gray-100 text-base font-normal text-gray-400">
                <div className="flex flex-col divide-y divide-gray-300">
                  <div>
                    {registrationStatus === "registered" &&
                    transactionStatus === "settlement" ? (
                      <span className="w-full text-center uppercase px-4 py-4 bg-cyan-600 text-white block transition-colors duration-300 whitespace-nowrap">
                        YOU ARE REGISTERED
                      </span>
                    ) : registrationStatus === "registered" &&
                      transactionStatus === "pending" ? (
                      <button
                        onClick={handleBayar}
                        className="w-full text-center uppercase px-4 py-4 block transition-colors duration-300 bg-cyan-600 font-normal text-white whitespace-nowrap hover:bg-cyan-800"
                        disabled={!isSnapReady || !snapToken}
                      >
                        COMPLETE PAYMENT
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRegister(selectedCategory)}
                        className={`w-full text-center uppercase px-4 py-4 block transition-colors duration-300 whitespace-nowrap ${
                          isRegistrationOpen
                            ? "bg-cyan-600 font-normal text-white hover:bg-cyan-800"
                            : "text-white bg-slate-400 opacity-50 cursor-not-allowed"
                        }`}
                        disabled={!isRegistrationOpen}
                      >
                        {isComingSoon ? "COMING SOON" : isRegistrationOpen ? "REGISTER THIS EVENT" : "REGISTRATION CLOSED"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
});

export default BMM;
